import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Modal,
  ProgressBar,
  Row,
  Spinner,
  Stack,
  Table,
} from 'react-bootstrap';
import axios from 'axios';
import { fetchData, postData, putData, deleteData, patchData } from '../../redux/slices/apiCallSlice';
import { selectApiUrls } from '../../redux/slices/apiUrlSlice';
import { mergeFunctionKeyHeaders } from '../../axios/functionKey';
import {
  augmentDeletePayloadFromError,
  buildEndpointOptions,
  dedupeResponseKeys,
  dedupeSearchResults,
  filterEndpointOptions,
  filterResponseData,
  findProjectRecords,
  getResponseCount,
  normalizeRequestError,
  parseDeleteRequestTargets,
  unwrapRecords,
} from './adminApiTestUtils';
import './AdminAPITest.css';

const ACTIONS = ['FETCH', 'POST', 'PUT', 'PATCH', 'DELETE'];
const BODY_ACTIONS = new Set(['POST', 'PUT', 'PATCH']);
const SEARCH_EXCLUDED_ENDPOINTS = new Set([
  'bookingLogFull',
  'changeLog',
  'criticalData',
  'fullSchedule',
  'generalProject',
  'globalSearch',
  'shippedProjects',
]);
const SEARCH_CONCURRENCY = 8;

const normalizePostRequestBody = (value) => {
  if (Array.isArray(value)) return value.map(normalizePostRequestBody);
  if (!value || typeof value !== 'object') return value;
  return Object.entries(value).reduce((result, [key, entryValue]) => {
    result[key === 'ProjectId' ? 'projectId' : key] = normalizePostRequestBody(entryValue);
    return result;
  }, {});
};

const runWithConcurrency = async (items, concurrency, worker) => {
  let nextIndex = 0;
  const runners = Array.from({ length: Math.min(concurrency, items.length) }, async () => {
    while (nextIndex < items.length) {
      const currentIndex = nextIndex;
      nextIndex += 1;
      await worker(items[currentIndex], currentIndex);
    }
  });
  await Promise.all(runners);
};

const getExampleRecord = (payload) => {
  const records = unwrapRecords(payload);
  if (records.length > 0) return records[0];
  if (payload && typeof payload === 'object') return payload;
  return null;
};

const ApiHandlerComponent = () => {
  const dispatch = useDispatch();
  const apiUrls = useSelector(selectApiUrls);
  const endpointOptions = useMemo(() => buildEndpointOptions(apiUrls), [apiUrls]);

  const [selectedEndpointKey, setSelectedEndpointKey] = useState('');
  const [endpointQuery, setEndpointQuery] = useState('');
  const [urlText, setUrlText] = useState('');
  const [action, setAction] = useState('FETCH');
  const [body, setBody] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [deleteRequestObject, setDeleteRequestObject] = useState('');
  const [responseData, setResponseData] = useState(null);
  const [responseQuery, setResponseQuery] = useState('');
  const [requestError, setRequestError] = useState('');
  const [requestMeta, setRequestMeta] = useState(null);
  const [isExecuting, setIsExecuting] = useState(false);
  const [notice, setNotice] = useState(null);

  const [projectId, setProjectId] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchMessage, setSearchMessage] = useState('');
  const [searchSummary, setSearchSummary] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchProgress, setSearchProgress] = useState(0);

  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const [modalTitle, setModalTitle] = useState('Object Details');
  const abortControllerRef = useRef(null);

  const selectedEndpoint = useMemo(
    () => endpointOptions.find((option) => option.key === selectedEndpointKey) || null,
    [endpointOptions, selectedEndpointKey]
  );
  const filteredEndpointOptions = useMemo(
    () => filterEndpointOptions(endpointOptions, endpointQuery),
    [endpointOptions, endpointQuery]
  );
  const displayedEndpointOptions = useMemo(() => {
    if (!selectedEndpoint || filteredEndpointOptions.some((option) => option.key === selectedEndpoint.key)) {
      return filteredEndpointOptions;
    }
    return [selectedEndpoint, ...filteredEndpointOptions];
  }, [filteredEndpointOptions, selectedEndpoint]);

  const visibleResponse = useMemo(
    () => filterResponseData(responseData, responseQuery),
    [responseData, responseQuery]
  );
  const responseCount = useMemo(() => getResponseCount(visibleResponse), [visibleResponse]);

  useEffect(() => () => abortControllerRef.current?.abort(), []);

  const selectEndpoint = (key) => {
    setSelectedEndpointKey(key);
    const endpoint = endpointOptions.find((option) => option.key === key);
    setUrlText(endpoint?.url || '');
    setRequestError('');
    setDeleteRequestObject('');
  };

  const parseRequestBody = () => {
    if (!BODY_ACTIONS.has(action)) return undefined;
    if (!body.trim()) throw new Error(`${action} requests require a JSON body.`);
    try {
      return JSON.parse(body);
    } catch (_) {
      throw new Error('The request body is not valid JSON.');
    }
  };

  const executeRequest = async () => {
    setRequestError('');
    setNotice(null);
    if (!urlText.trim()) {
      setRequestError('Select an endpoint or enter a URL before executing the request.');
      return;
    }

    let parsedBody;
    let deleteTargets = [];
    try {
      parsedBody = parseRequestBody();
      if (action === 'DELETE') deleteTargets = parseDeleteRequestTargets(selectedId, deleteRequestObject);
    } catch (error) {
      setRequestError(error.message);
      return;
    }

    if (action === 'DELETE') {
      const confirmed = window.confirm(
        `Delete ${deleteTargets.length} record${deleteTargets.length === 1 ? '' : 's'} from this endpoint? This cannot be undone.`
      );
      if (!confirmed) return;
    }

    const startedAt = performance.now();
    setIsExecuting(true);
    try {
      let result;
      switch (action) {
        case 'FETCH':
          result = await dispatch(fetchData({
            url: urlText.trim(),
            contract: {
              response: {
                keepOriginalKeys: true,
                includeMappedKeys: false,
                dedupeCaseVariantKeys: true,
              },
            },
          })).unwrap();
          break;
        case 'POST':
          result = await dispatch(postData({
            url: urlText.trim(),
            data: normalizePostRequestBody(parsedBody),
            contract: { requestCase: 'raw' },
          })).unwrap();
          break;
        case 'PUT':
          result = await dispatch(putData({ url: urlText.trim(), data: parsedBody })).unwrap();
          break;
        case 'PATCH':
          result = await dispatch(patchData({
            url: urlText.trim(),
            data: parsedBody,
            allowFullPayload: true,
          })).unwrap();
          break;
        case 'DELETE': {
          const deleteResponses = [];
          let referenceResponse = responseData;
          const getDeleteReferenceResponse = async () => {
            if (unwrapRecords(referenceResponse).length > 0) return referenceResponse;
            referenceResponse = await dispatch(fetchData({
              url: urlText.trim(),
              contract: {
                response: {
                  keepOriginalKeys: true,
                  includeMappedKeys: false,
                  dedupeCaseVariantKeys: true,
                },
              },
            })).unwrap();
            return referenceResponse;
          };

          for (const target of deleteTargets) {
            let deletePayload = { ...target.payload };
            const recoveredFields = [];
            let recoveryAttempt = 0;

            while (recoveryAttempt < 6) {
              try {
                const deleteResponse = await dispatch(deleteData({
                  url: urlText.trim(),
                  deleteId: deletePayload,
                })).unwrap();
                deleteResponses.push({
                  id: target.label,
                  request: deletePayload,
                  recoveredFields,
                  response: deleteResponse,
                });
                break;
              } catch (deleteError) {
                recoveryAttempt += 1;
                try {
                  await getDeleteReferenceResponse();
                } catch {
                  // The validation response may still provide enough detail
                  // to update the object without an example record.
                }

                const recovery = augmentDeletePayloadFromError(
                  deleteError,
                  deletePayload,
                  referenceResponse,
                  target.lookupValue
                );
                if (!recovery) throw deleteError;

                deletePayload = recovery.payload;
                setDeleteRequestObject(JSON.stringify(deletePayload, null, 2));
                recoveredFields.push(...recovery.resolvedFields.filter((field) => !recoveredFields.includes(field)));

                if (recovery.unresolvedFields.length > 0) {
                  const recoveryError = new Error(
                    `The API requires ${recovery.unresolvedFields.join(', ')}. The DELETE request object was updated; enter the missing value and execute again.`
                  );
                  recoveryError.deletePayload = deletePayload;
                  recoveryError.apiError = deleteError;
                  throw recoveryError;
                }

                if (recoveryAttempt >= 6) throw deleteError;
              }
            }
          }
          result = { deleted: deleteTargets.length, records: deleteResponses };
          break;
        }
        default:
          throw new Error('Unsupported action.');
      }

      const durationMs = Math.round(performance.now() - startedAt);
      setResponseData(dedupeResponseKeys(result));
      setResponseQuery('');
      setRequestMeta({ action, durationMs, completedAt: new Date() });
      setNotice({ variant: 'success', message: `${action} completed in ${durationMs} ms.` });
    } catch (error) {
      setRequestError(normalizeRequestError(error));
      if (error.deletePayload) {
        setResponseData(dedupeResponseKeys({
          error: error.apiError?.details || error.apiError?.message || error.message,
          deleteRequestObject: error.deletePayload,
        }));
        setResponseQuery('');
      }
      setRequestMeta(null);
    } finally {
      setIsExecuting(false);
    }
  };

  const fetchExample = async () => {
    setRequestError('');
    setNotice(null);
    if (!urlText.trim()) {
      setRequestError('Select an endpoint or enter a URL before fetching an example.');
      return;
    }

    setIsExecuting(true);
    try {
      const response = await axios.get(urlText.trim(), {
        timeout: 10000,
        headers: mergeFunctionKeyHeaders({}, urlText.trim()),
      });
      const example = getExampleRecord(response.data);
      if (!example) {
        setNotice({ variant: 'warning', message: 'The endpoint returned no example record.' });
        return;
      }
      setModalContent(dedupeResponseKeys(example));
      setModalTitle(`Example · ${selectedEndpoint?.key || 'Custom endpoint'}`);
      setShowModal(true);
    } catch (error) {
      setRequestError(normalizeRequestError(error));
    } finally {
      setIsExecuting(false);
    }
  };

  const copyJson = async (content, successMessage) => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(content, null, 2));
      setNotice({ variant: 'success', message: successMessage });
    } catch (error) {
      setNotice({ variant: 'danger', message: `Unable to copy JSON: ${error.message}` });
    }
  };

  const searchableEndpoints = useMemo(() => endpointOptions.filter((option) => (
    !SEARCH_EXCLUDED_ENDPOINTS.has(option.key) &&
    !option.key.startsWith('sp')
  )), [endpointOptions]);

  const searchProjectId = async (event) => {
    event.preventDefault();
    if (!projectId.trim()) {
      setSearchMessage('Enter a ProjectID to search.');
      return;
    }

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const results = [];
    let completed = 0;
    let succeeded = 0;
    let failed = 0;

    setIsSearching(true);
    setSearchProgress(0);
    setSearchResults([]);
    setSearchMessage('');
    setSearchSummary(null);

    await runWithConcurrency(searchableEndpoints, SEARCH_CONCURRENCY, async (endpoint) => {
      if (controller.signal.aborted) return;
      try {
        const response = await axios.get(endpoint.url, {
          signal: controller.signal,
          timeout: 5000,
          headers: mergeFunctionKeyHeaders({}, endpoint.url),
        });
        succeeded += 1;
        results.push(...findProjectRecords(response.data, projectId, endpoint.key));
      } catch (error) {
        if (!controller.signal.aborted) failed += 1;
      } finally {
        completed += 1;
        setSearchProgress((completed / searchableEndpoints.length) * 100);
      }
    });

    if (controller.signal.aborted) {
      setSearchMessage('Search canceled.');
    } else {
      const uniqueResults = dedupeSearchResults(results);
      setSearchResults(uniqueResults);
      setSearchSummary({ scanned: searchableEndpoints.length, succeeded, failed });
      setSearchMessage(uniqueResults.length > 0
        ? `Found ${uniqueResults.length} matching record${uniqueResults.length === 1 ? '' : 's'}.`
        : `No records found for ProjectID ${projectId}.`);
    }

    setIsSearching(false);
    setSearchProgress(0);
    abortControllerRef.current = null;
  };

  const cancelSearch = () => abortControllerRef.current?.abort();

  const clearRequest = () => {
    setResponseData(null);
    setResponseQuery('');
    setRequestError('');
    setRequestMeta(null);
    setNotice(null);
    setDeleteRequestObject('');
  };

  return (
    <div className="admin-api-test">
      <div className="admin-api-test__heading">
        <div>
          <h2>API Test Console</h2>
          <p>Inspect endpoints, execute controlled requests, and locate project records across production APIs.</p>
        </div>
        <Badge bg="secondary">{endpointOptions.length} unique endpoints</Badge>
      </div>

      {notice && (
        <Alert variant={notice.variant} dismissible onClose={() => setNotice(null)}>
          {notice.message}
        </Alert>
      )}

      <Row className="g-3 align-items-stretch">
        <Col xs={12} xl={5}>
          <Card className="admin-api-test__card h-100">
            <Card.Header>
              <div>
                <strong>Request Builder</strong>
                <small>Choose an endpoint or edit its URL directly.</small>
              </div>
            </Card.Header>
            <Card.Body>
              <Form.Group className="mb-3" controlId="api-endpoint-search">
                <Form.Label>Search endpoints</Form.Label>
                <InputGroup>
                  <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
                  <Form.Control
                    value={endpointQuery}
                    onChange={(event) => setEndpointQuery(event.target.value)}
                    placeholder="Search by name or URL"
                  />
                  {endpointQuery && (
                    <Button variant="outline-secondary" onClick={() => setEndpointQuery('')} aria-label="Clear endpoint search">
                      <i className="bi bi-x-lg" />
                    </Button>
                  )}
                </InputGroup>
                <Form.Text>{filteredEndpointOptions.length} matching endpoints</Form.Text>
              </Form.Group>

              <Form.Group className="mb-3" controlId="api-endpoint-select">
                <Form.Label>API endpoint</Form.Label>
                <Form.Select value={selectedEndpointKey} onChange={(event) => selectEndpoint(event.target.value)}>
                  <option value="">Select an endpoint...</option>
                  {displayedEndpointOptions.map((endpoint) => (
                    <option key={endpoint.key} value={endpoint.key}>
                      {endpoint.key}{endpoint.aliases.length ? ` (+${endpoint.aliases.length} alias)` : ''}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3" controlId="api-request-url">
                <Form.Label>Request URL</Form.Label>
                <Form.Control
                  className="admin-api-test__mono"
                  value={urlText}
                  onChange={(event) => setUrlText(event.target.value)}
                  placeholder="https://..."
                />
              </Form.Group>

              <Form.Group className="mb-3" controlId="api-request-action">
                <Form.Label>Method</Form.Label>
                <div className="admin-api-test__actions" role="group" aria-label="Request method">
                  {ACTIONS.map((requestAction) => (
                    <Button
                      key={requestAction}
                      size="sm"
                      variant={action === requestAction
                        ? (requestAction === 'DELETE' ? 'danger' : 'primary')
                        : 'outline-secondary'}
                      onClick={() => {
                        setAction(requestAction);
                        setRequestError('');
                      }}
                    >
                      {requestAction === 'FETCH' ? 'GET' : requestAction}
                    </Button>
                  ))}
                </div>
              </Form.Group>

              {BODY_ACTIONS.has(action) && (
                <Form.Group className="mb-3" controlId="api-request-body">
                  <Form.Label>Request body</Form.Label>
                  <Form.Control
                    as="textarea"
                    className="admin-api-test__mono admin-api-test__editor"
                    value={body}
                    onChange={(event) => setBody(event.target.value)}
                    placeholder={'{\n  "key": "value"\n}'}
                    spellCheck={false}
                    isInvalid={Boolean(requestError && requestError.toLowerCase().includes('json'))}
                  />
                </Form.Group>
              )}

              {action === 'DELETE' && (
                <>
                  <Form.Group className="mb-3" controlId="api-delete-id">
                    <Form.Label>ID or range</Form.Label>
                    <Form.Control
                      value={selectedId}
                      onChange={(event) => setSelectedId(event.target.value)}
                      placeholder="Example: 42 or 42-48"
                    />
                    <Form.Text>Ranges are limited to 100 records and require confirmation.</Form.Text>
                  </Form.Group>
                  <Form.Group className="mb-3" controlId="api-delete-object">
                    <Form.Label>DELETE request object <span className="text-muted">(optional)</span></Form.Label>
                    <Form.Control
                      as="textarea"
                      className="admin-api-test__mono admin-api-test__editor"
                      value={deleteRequestObject}
                      onChange={(event) => setDeleteRequestObject(event.target.value)}
                      placeholder={'{\n  "PartId": 42,\n  "StationId": 11\n}'}
                      spellCheck={false}
                    />
                    <Form.Text>
                      This object takes precedence over the ID field. If the API reports required keys, the console reads the current GET response and updates this object automatically.
                    </Form.Text>
                  </Form.Group>
                </>
              )}

              {requestError && <Alert variant="danger" className="py-2">{requestError}</Alert>}

              <Stack direction="horizontal" gap={2} className="flex-wrap">
                <Button onClick={executeRequest} disabled={isExecuting || isSearching || !urlText.trim()}>
                  {isExecuting ? <Spinner animation="border" size="sm" className="me-2" /> : <i className="bi bi-play-fill me-2" />}
                  Execute
                </Button>
                <Button variant="outline-primary" onClick={fetchExample} disabled={isExecuting || isSearching || !urlText.trim()}>
                  <i className="bi bi-braces me-2" />
                  Fetch Example
                </Button>
                <Button variant="outline-secondary" onClick={clearRequest} disabled={isExecuting}>
                  Clear
                </Button>
              </Stack>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} xl={7}>
          <Card className="admin-api-test__card h-100">
            <Card.Header className="admin-api-test__response-header">
              <div>
                <strong>Response</strong>
                <small>{requestMeta ? `${requestMeta.action} · ${requestMeta.durationMs} ms` : 'No request executed yet'}</small>
              </div>
              <Stack direction="horizontal" gap={2}>
                <Badge bg={responseCount > 0 ? 'success' : 'secondary'}>{responseCount} result{responseCount === 1 ? '' : 's'}</Badge>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => copyJson(visibleResponse, 'Response JSON copied.')}
                  disabled={responseData === null}
                  title="Copy response JSON"
                >
                  <i className="bi bi-clipboard" />
                </Button>
              </Stack>
            </Card.Header>
            <Card.Body className="admin-api-test__response-body">
              <InputGroup className="mb-3">
                <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
                <Form.Control
                  value={responseQuery}
                  onChange={(event) => setResponseQuery(event.target.value)}
                  placeholder="Filter response records or keys"
                  disabled={responseData === null}
                />
              </InputGroup>
              <pre className="admin-api-test__response">
                {responseData === null
                  ? 'Execute a request to view its response.'
                  : JSON.stringify(visibleResponse, null, 2)}
              </pre>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="admin-api-test__card mt-3">
        <Card.Header>
          <div>
            <strong>ProjectID Search</strong>
            <small>Searches production record endpoints in batches of {SEARCH_CONCURRENCY}.</small>
          </div>
        </Card.Header>
        <Card.Body>
          <Form onSubmit={searchProjectId}>
            <Row className="g-2 align-items-end">
              <Col xs={12} md={7} lg={5}>
                <Form.Group controlId="api-project-search">
                  <Form.Label>ProjectID</Form.Label>
                  <Form.Control
                    inputMode="numeric"
                    value={projectId}
                    onChange={(event) => setProjectId(event.target.value)}
                    placeholder="Enter ProjectID"
                    disabled={isSearching}
                  />
                </Form.Group>
              </Col>
              <Col xs="auto">
                <Button type="submit" disabled={isSearching || !projectId.trim()}>
                  {isSearching && <Spinner animation="border" size="sm" className="me-2" />}
                  Search APIs
                </Button>
              </Col>
              {isSearching && (
                <Col xs="auto">
                  <Button variant="outline-danger" onClick={cancelSearch}>Cancel</Button>
                </Col>
              )}
            </Row>
          </Form>

          {isSearching && (
            <ProgressBar
              className="mt-3"
              now={searchProgress}
              label={`${Math.round(searchProgress)}%`}
              animated
            />
          )}

          {searchMessage && (
            <Alert variant={searchResults.length > 0 ? 'success' : 'info'} className="mt-3 mb-0 py-2">
              <div className="d-flex flex-wrap justify-content-between gap-2">
                <span>{searchMessage}</span>
                {searchSummary && (
                  <span className="small">
                    {searchSummary.scanned} scanned · {searchSummary.succeeded} succeeded · {searchSummary.failed} failed
                  </span>
                )}
              </div>
            </Alert>
          )}

          {searchResults.length > 0 && (
            <div className="admin-api-test__search-results mt-3">
              <Table striped bordered hover responsive className="mb-0">
                <thead>
                  <tr>
                    <th>Endpoint</th>
                    <th>ID</th>
                    <th>Zone</th>
                    <th className="text-end">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {searchResults.map((result) => (
                    <tr key={`${result.tableName}-${result.id}-${result.zone}`}>
                      <td>{result.tableName}</td>
                      <td>{result.id || '—'}</td>
                      <td>{result.zone || '—'}</td>
                      <td className="text-end">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => {
                            setModalContent(result.content);
                            setModalTitle(`${result.tableName} · ${result.id || 'Record'}`);
                            setShowModal(true);
                          }}
                        >
                          View JSON
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title>{modalTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <pre className="admin-api-test__modal-json">{JSON.stringify(modalContent, null, 2)}</pre>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-primary" onClick={() => copyJson(modalContent, 'Object JSON copied.')}>
            <i className="bi bi-clipboard me-2" />
            Copy JSON
          </Button>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Close</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ApiHandlerComponent;
